/**
 * Session History - View all past interview sessions
 */

import React, { useState, useEffect, useCallback } from 'react';
import type { Plan } from '../utils/accountApi';
import { getUserPlan, hasProFeatures } from '../utils/plans';

const API_URL =
  import.meta.env.VITE_API_URL ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:5000'
    : 'https://krow7dgex4.execute-api.eu-west-2.amazonaws.com');

interface User {
  username?: string;
  email?: string;
  accountType?: 'legacy' | 'account';
  account?: { plan: Plan } | null;
}

interface SessionInterview {
  type: string;
  name: string;
  duration: number;
}

interface SessionFeedback {
  score?: number | null;
  summary?: string;
  improvements?: string[] | null;
  strengths?: string[] | null;
}

interface Message {
  sender: 'user' | 'ai' | 'system' | 'error';
  text: string;
  timestamp: string;
}

interface Session {
  sessionId: string;
  date: string;
  interviews?: SessionInterview[];
  feedbacks?: SessionFeedback[];
  totalDuration: number;
  messages?: Message[];
}

interface SessionHistoryProps {
  user: User;
  onBack: () => void;
  onViewSession?: (sessionId: string) => void;
}

const SessionHistory: React.FC<SessionHistoryProps> = ({
  user,
  onBack,
  onViewSession: _onViewSession,
}) => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const canExport = hasProFeatures(getUserPlan(user));

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const username =
        user?.username || user?.email?.split('@')[0] || 'unknown';

      // Try to fetch from API first
      try {
        const response = await fetch(
          `${API_URL}/sessions?username=${encodeURIComponent(username)}`
        );
        if (response.ok) {
          const data = await response.json();
          const sessions = data.sessions || [];
          
          // Log session structure for debugging
          console.log('📊 Sessions fetched from API:', {
            count: sessions.length,
            firstSession: sessions.length > 0 ? {
              sessionId: sessions[0].sessionId,
              feedbacksCount: sessions[0].feedbacks?.length || 0,
              firstFeedback: sessions[0].feedbacks?.[0] ? {
                hasScore: sessions[0].feedbacks[0].score !== undefined,
                hasSummary: !!sessions[0].feedbacks[0].summary,
                hasImprovements: Array.isArray(sessions[0].feedbacks[0].improvements),
                improvementsCount: sessions[0].feedbacks[0].improvements?.length || 0,
                hasStrengths: Array.isArray(sessions[0].feedbacks[0].strengths),
                strengthsCount: sessions[0].feedbacks[0].strengths?.length || 0,
                keys: Object.keys(sessions[0].feedbacks[0] || {}),
              } : null,
            } : null,
          });
          
          setSessions(sessions);
          setLoading(false);
          return;
        }
      } catch (apiError) {
        console.warn('API fetch failed, using localStorage:', apiError);
      }

      // Fallback to localStorage
      const storedSessions = localStorage.getItem(
        `interview_sessions_${username}`
      );
      if (storedSessions) {
        setSessions(JSON.parse(storedSessions));
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError('Failed to load sessions');
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSessions();
    
    // Close export menus when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[id^="export-menu-"]') && !target.closest('button[onclick*="export-menu"]')) {
        document.querySelectorAll('[id^="export-menu-"]').forEach((menu) => {
          menu.classList.add('hidden');
        });
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [fetchSessions]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (minutes: number): string => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}hr ${mins}min` : `${hours}hr`;
    }
    return `${minutes}min`;
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const getOverallScore = (feedbacks?: SessionFeedback[]): number | null => {
    if (!feedbacks || feedbacks.length === 0) return null;
    const validScores = feedbacks.filter(
      (f) => f.score !== null && f.score !== undefined
    );
    if (validScores.length === 0) return null;
    return Math.round(
      validScores.reduce((sum, f) => sum + (f.score || 0), 0) /
        validScores.length
    );
  };

  const getInterviewIcon = (type?: string): string => {
    const icons: Record<string, string> = {
      system_design: '🏗️',
      coding: '💻',
      technical: '🎯',
      architecture_review: '📐',
      behavioural: '🗣️',
    };
    return icons[type || ''] || '📝';
  };

  const exportTranscript = (session: Session, format: 'markdown' | 'json' | 'txt') => {
    if (!session.messages || session.messages.length === 0) {
      alert('No transcript available for this session.');
      return;
    }

    let content = '';
    let filename = '';
    let mimeType = '';

    if (format === 'markdown') {
      filename = `interview_transcript_${session.sessionId}_${formatDate(session.date).replace(/[^a-zA-Z0-9]/g, '_')}.md`;
      mimeType = 'text/markdown';
      
      content = `# Interview Transcript\n\n`;
      content += `**Session ID:** ${session.sessionId}\n`;
      content += `**Date:** ${formatDate(session.date)}\n`;
      content += `**Duration:** ${formatDuration(session.totalDuration)}\n`;
      content += `**Interview Types:** ${session.interviews?.map(i => i.name).join(', ') || 'N/A'}\n\n`;
      content += `---\n\n`;
      content += `## Conversation\n\n`;
      
      session.messages.forEach((msg) => {
        const senderLabel = msg.sender === 'user' ? '**You:**' : msg.sender === 'ai' ? '**AI:**' : msg.sender === 'system' ? '**System:**' : '**Error:**';
        const timestamp = new Date(msg.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        content += `${senderLabel} (${timestamp})\n${msg.text}\n\n`;
      });

      if (session.feedbacks && session.feedbacks.length > 0) {
        content += `---\n\n`;
        content += `## Feedback Summary\n\n`;
        session.feedbacks.forEach((feedback, idx) => {
          const interviewName = session.interviews?.[idx]?.name || `Interview ${idx + 1}`;
          content += `### ${interviewName}\n\n`;
          if (feedback.score !== null && feedback.score !== undefined) {
            content += `**Score:** ${feedback.score}%\n\n`;
          }
          if (feedback.summary) {
            content += `**Summary:** ${feedback.summary}\n\n`;
          }
          if (feedback.strengths && feedback.strengths.length > 0) {
            content += `**Strengths:**\n`;
            feedback.strengths.forEach((strength) => {
              content += `- ✓ ${strength}\n`;
            });
            content += `\n`;
          }
          if (feedback.improvements && feedback.improvements.length > 0) {
            content += `**Areas for Improvement:**\n`;
            feedback.improvements.forEach((improvement) => {
              content += `- • ${improvement}\n`;
            });
            content += `\n`;
          }
          content += `\n`;
        });
      }
    } else if (format === 'json') {
      filename = `interview_transcript_${session.sessionId}_${formatDate(session.date).replace(/[^a-zA-Z0-9]/g, '_')}.json`;
      mimeType = 'application/json';
      content = JSON.stringify({
        sessionId: session.sessionId,
        date: session.date,
        duration: session.totalDuration,
        interviews: session.interviews,
        messages: session.messages,
        feedbacks: session.feedbacks,
      }, null, 2);
    } else if (format === 'txt') {
      filename = `interview_transcript_${session.sessionId}_${formatDate(session.date).replace(/[^a-zA-Z0-9]/g, '_')}.txt`;
      mimeType = 'text/plain';
      
      content = `Interview Transcript\n`;
      content += `===================\n\n`;
      content += `Session ID: ${session.sessionId}\n`;
      content += `Date: ${formatDate(session.date)}\n`;
      content += `Duration: ${formatDuration(session.totalDuration)}\n`;
      content += `Interview Types: ${session.interviews?.map(i => i.name).join(', ') || 'N/A'}\n\n`;
      content += `${'='.repeat(50)}\n\n`;
      content += `CONVERSATION\n\n`;
      
      session.messages.forEach((msg) => {
        const senderLabel = msg.sender === 'user' ? '[YOU]' : msg.sender === 'ai' ? '[AI]' : msg.sender === 'system' ? '[SYSTEM]' : '[ERROR]';
        const timestamp = new Date(msg.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        content += `${senderLabel} (${timestamp}): ${msg.text}\n\n`;
      });

      if (session.feedbacks && session.feedbacks.length > 0) {
        content += `${'='.repeat(50)}\n\n`;
        content += `FEEDBACK SUMMARY\n\n`;
        session.feedbacks.forEach((feedback, idx) => {
          const interviewName = session.interviews?.[idx]?.name || `Interview ${idx + 1}`;
          content += `${interviewName}\n`;
          content += `${'-'.repeat(50)}\n`;
          if (feedback.score !== null && feedback.score !== undefined) {
            content += `Score: ${feedback.score}%\n`;
          }
          if (feedback.summary) {
            content += `Summary: ${feedback.summary}\n`;
          }
          if (feedback.strengths && feedback.strengths.length > 0) {
            content += `Strengths:\n`;
            feedback.strengths.forEach((strength) => {
              content += `  ✓ ${strength}\n`;
            });
          }
          if (feedback.improvements && feedback.improvements.length > 0) {
            content += `Areas for Improvement:\n`;
            feedback.improvements.forEach((improvement) => {
              content += `  • ${improvement}\n`;
            });
          }
          content += `\n`;
        });
      }
    }

    // Create and download file
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-5">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col items-center justify-center py-60 text-gray-700">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin mb-5"></div>
            <p className="text-gray-600">Loading sessions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-5">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 sm:mb-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
            <button
              className="self-start bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg text-sm font-medium transition-all duration-200 hover:shadow-md"
              onClick={onBack}
            >
              ← Back
            </button>
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-3xl sm:text-4xl text-gray-900 font-bold mb-2">
                📊 Interview History
              </h1>
              <p className="text-gray-600 text-base sm:text-lg">
                View your past interview sessions and feedback
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-lg mb-5 text-center">
            {error}
          </div>
        )}

        {sessions.length === 0 ? (
          <div className="text-center py-60 px-5 bg-white rounded-2xl border-2 border-dashed border-gray-300 shadow-sm">
            <div className="text-7xl mb-5">📋</div>
            <h3 className="text-gray-900 text-2xl mb-2.5 font-semibold">
              No sessions yet
            </h3>
            <p className="text-gray-600 mb-8">
              Complete your first interview session to see it here
            </p>
            <button
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-8 py-3.5 rounded-lg text-base font-semibold cursor-pointer transition-all duration-200 hover:shadow-lg"
              onClick={onBack}
            >
              Start Interview
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {sessions.map((session) => {
              const overallScore = getOverallScore(session.feedbacks);
              return (
                  <div
                    key={session.sessionId}
                    className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0 mb-4">
                      <div className="text-gray-600 text-sm">
                        {formatDate(session.date)}
                      </div>
                      {overallScore !== null && (
                        <div
                          className="text-2xl sm:text-3xl font-bold"
                          style={{ color: getScoreColor(overallScore) }}
                        >
                          {overallScore}%
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2.5 mb-4">
                      {session.interviews?.map((interview, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 px-3.5 py-2 bg-indigo-50 rounded-full border border-indigo-200"
                        >
                          <span className="text-lg">
                            {getInterviewIcon(interview.type)}
                          </span>
                          <span className="text-gray-900 font-medium text-sm">
                            {interview.name}
                          </span>
                          <span className="text-gray-500 text-xs">
                            {formatDuration(interview.duration)}
                          </span>
                          {session.feedbacks?.[idx]?.score !== undefined &&
                            session.feedbacks?.[idx]?.score !== null && (
                              <span
                                className="font-bold text-xs"
                                style={{
                                  color: getScoreColor(
                                    session.feedbacks![idx].score!
                                  ),
                                }}
                              >
                                {session.feedbacks![idx].score}%
                              </span>
                            )}
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-wrap gap-4 sm:gap-6 pt-3 border-t border-gray-200 items-center justify-between">
                      <div className="flex flex-wrap gap-4 sm:gap-6">
                        <span className="text-gray-600 text-sm">
                          <span className="text-gray-500 font-medium">Duration:</span>{' '}
                          {formatDuration(session.totalDuration)}
                        </span>
                        <span className="text-gray-600 text-sm">
                          <span className="text-gray-500 font-medium">Interviews:</span>{' '}
                          {session.interviews?.length || 0}
                        </span>
                      </div>
                      {canExport && session.messages && session.messages.length > 0 && (
                        <div className="relative group">
                          <button
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 hover:shadow-md flex items-center gap-1.5"
                            onClick={(e) => {
                              e.stopPropagation();
                              const menu = document.getElementById(`export-menu-${session.sessionId}`);
                              if (menu) {
                                // Close all other menus
                                document.querySelectorAll('[id^="export-menu-"]').forEach((m) => {
                                  if (m.id !== `export-menu-${session.sessionId}`) {
                                    m.classList.add('hidden');
                                  }
                                });
                                menu.classList.toggle('hidden');
                              }
                            }}
                          >
                            📥 Export Transcript
                            <span className="text-xs">▼</span>
                          </button>
                          <div
                            id={`export-menu-${session.sessionId}`}
                            className="hidden absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-10 py-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                exportTranscript(session, 'markdown');
                                const menu = document.getElementById(`export-menu-${session.sessionId}`);
                                if (menu) menu.classList.add('hidden');
                              }}
                            >
                              📄 Export as Markdown
                            </button>
                            <button
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                exportTranscript(session, 'json');
                                const menu = document.getElementById(`export-menu-${session.sessionId}`);
                                if (menu) menu.classList.add('hidden');
                              }}
                            >
                              📋 Export as JSON
                            </button>
                            <button
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                exportTranscript(session, 'txt');
                                const menu = document.getElementById(`export-menu-${session.sessionId}`);
                                if (menu) menu.classList.add('hidden');
                              }}
                            >
                              📝 Export as Text
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {session.feedbacks && session.feedbacks.length > 0 && (
                      <div className="mt-5 pt-4 border-t border-gray-200">
                        <h4 className="text-gray-900 text-sm mb-4 uppercase tracking-wide font-semibold">
                          Feedback Summary
                        </h4>
                        <div className="flex flex-col gap-4">
                          {session.feedbacks.map((feedback, idx) => {
                            const interviewName = session.interviews?.[idx]?.name || `Interview ${idx + 1}`;
                            const interviewType = session.interviews?.[idx]?.type;
                            
                            return (
                              <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2 mb-3">
                                  <span className="text-lg">
                                    {getInterviewIcon(interviewType)}
                                  </span>
                                  <span className="text-gray-900 font-semibold text-base flex-1">
                                    {interviewName}
                                  </span>
                                  {feedback.score !== null && feedback.score !== undefined && (
                                    <span
                                      className="text-xl sm:text-2xl font-bold px-3 py-1 rounded-lg"
                                      style={{
                                        color: getScoreColor(feedback.score),
                                        backgroundColor: `${getScoreColor(feedback.score)}15`,
                                      }}
                                    >
                                      {feedback.score}%
                                    </span>
                                  )}
                                </div>
                                
                                {feedback.summary && feedback.summary.trim() && (
                                  <div className="mb-3">
                                    <p className="text-gray-700 text-sm leading-relaxed">
                                      <span className="font-semibold text-gray-900">Summary: </span>
                                      {feedback.summary}
                                    </p>
                                  </div>
                                )}
                                
                                {feedback.strengths && Array.isArray(feedback.strengths) && feedback.strengths.length > 0 && (
                                  <div className="mb-3">
                                    <h5 className="text-emerald-600 text-xs font-semibold uppercase tracking-wide mb-2">
                                      ✓ Strengths
                                    </h5>
                                    <ul className="list-none p-0 m-0 space-y-1.5">
                                      {feedback.strengths.map((strength: string, strengthIdx: number) => (
                                        <li
                                          key={strengthIdx}
                                          className="text-gray-700 text-sm leading-relaxed pl-4 relative before:content-['✓'] before:absolute before:left-0 before:text-emerald-600 before:font-bold"
                                        >
                                          {strength}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                {feedback.improvements && Array.isArray(feedback.improvements) && feedback.improvements.length > 0 && (
                                  <div>
                                    <h5 className="text-red-600 text-xs font-semibold uppercase tracking-wide mb-2">
                                      ⚠ Areas for Improvement
                                    </h5>
                                    <ul className="list-none p-0 m-0 space-y-1.5">
                                      {feedback.improvements.map((improvement: string, improvementIdx: number) => (
                                        <li
                                          key={improvementIdx}
                                          className="text-gray-700 text-sm leading-relaxed pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-red-600 before:font-bold before:text-lg"
                                        >
                                          {improvement}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                {(!feedback.summary || !feedback.summary.trim()) && 
                                 (!feedback.strengths || !Array.isArray(feedback.strengths) || feedback.strengths.length === 0) && 
                                 (!feedback.improvements || !Array.isArray(feedback.improvements) || feedback.improvements.length === 0) && (
                                  <p className="text-gray-500 text-sm italic">
                                    No detailed feedback available for this interview.
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionHistory;
